/**
 * Pure client-side model for a Task Intelligence review session.
 *
 * Analysis is ephemeral: nothing here is persisted. A draft only becomes a
 * real row once the user approves it and the UI calls createProjectTask /
 * updateProjectTask — the same functions the manual Task Editor uses.
 */
import { validateTaskInput } from "../helpers/tasks/taskModel";

export const TASK_ACTION = Object.freeze({
  CREATE: "create",
  UPDATE: "update",
  SUGGEST: "suggest",
  NO_ACTION: "no_action",
  CLARIFY: "clarify",
});

let draftCounter = 0;
function nextDraftId() {
  draftCounter += 1;
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `draft-${Date.now()}-${draftCounter}`;
}

/**
 * Raw validated server draft → review-ready client draft. `no_action` items
 * are kept out of the selectable list entirely (see splitDrafts below); this
 * only shapes the ones a human might act on.
 */
export function toReviewDraft(raw) {
  const action = raw.action;
  return {
    id: nextDraftId(),
    group: raw.group || "General",
    title: raw.title || "",
    description: raw.description || "",
    action,
    explicit: raw.explicit !== false,
    assigneeId: raw.assigneeId || "",
    assigneeName: raw.assigneeName || "",
    dueDate: raw.dueDate || "",
    priority: raw.priority || "",
    locationId: raw.locationId || "",
    existingTaskId: raw.existingTaskId || null,
    clarifyPrompt: raw.clarifyPrompt || null,
    evidence: raw.evidence || "",
    // Clarify items start unselected — a human must resolve them first.
    selected: action !== TASK_ACTION.CLARIFY,
  };
}

/** Split the model's drafts into what the review screen shows selectable vs. informational. */
export function splitDrafts(rawDrafts = []) {
  const actionable = [];
  const noAction = [];
  rawDrafts.forEach((raw) => {
    if (raw.action === TASK_ACTION.NO_ACTION) {
      noAction.push(raw);
    } else {
      actionable.push(toReviewDraft(raw));
    }
  });
  return { drafts: actionable, noAction };
}

/** Group drafts by their `group` label, preserving first-seen order. */
export function groupDrafts(drafts = []) {
  const order = [];
  const byGroup = new Map();
  drafts.forEach((draft) => {
    const key = draft.group || "General";
    if (!byGroup.has(key)) {
      byGroup.set(key, []);
      order.push(key);
    }
    byGroup.get(key).push(draft);
  });
  return order.map((group) => ({ group, drafts: byGroup.get(group) }));
}

/** Apply a patch to every selected draft (immutable). */
export function applyBulkPatch(drafts, selectedIds, patch) {
  const ids = selectedIds instanceof Set ? selectedIds : new Set(selectedIds);
  return drafts.map((draft) => (ids.has(draft.id) ? { ...draft, ...patch } : draft));
}

/** Replace one draft by id (immutable) — used by Quick Edit. */
export function updateDraftById(drafts, id, patch) {
  return drafts.map((draft) => (draft.id === id ? { ...draft, ...patch } : draft));
}

/**
 * A draft is approvable once it has a valid title/description/priority/date
 * and, for "update", still references an existing task. "clarify" drafts
 * become approvable the moment a human fills in the missing piece (the UI
 * clears clarifyPrompt and flips the action to "create" when that happens).
 */
export function draftValidationErrors(draft) {
  if (draft.action === TASK_ACTION.CLARIFY) {
    return { clarify: draft.clarifyPrompt || "This task needs more information before it can be created." };
  }
  if (draft.action === TASK_ACTION.UPDATE && !draft.existingTaskId) {
    return { update: "The task this would update could not be found." };
  }
  const { ok, errors } = validateTaskInput({
    title: draft.title,
    description: draft.description,
    priority: draft.priority,
    dueDate: draft.dueDate,
  });
  return ok ? {} : errors;
}

export function isDraftApprovable(draft) {
  return Object.keys(draftValidationErrors(draft)).length === 0;
}

/** Fields accepted by createProjectTask / updateProjectTask. */
export function draftToTaskInput(draft) {
  return {
    title: draft.title,
    description: draft.description || "",
    locationId: draft.locationId || "",
    assigneeId: draft.assigneeId || "",
    dueDate: draft.dueDate || "",
    priority: draft.priority || "",
  };
}

/** A human has resolved a clarify item (e.g. filled in the title) — promote it to create. */
export function resolveClarifyDraft(draft, patch) {
  return { ...draft, ...patch, action: TASK_ACTION.CREATE, clarifyPrompt: null, selected: true };
}
